import type { FileId } from "@inrev/common";
import { Option } from "@inrev/core";
import { documentUrlQueryKey, noDocumentUrlQueryKey } from "@portal/api/files";
import { type QueryKey, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useRequest } from "../../../utils/request";

/** Where to fetch a file's signed URL, and the query key to cache it under. */
export type PDFViewerControl = {
	fetchUrl: string;
	queryKey: QueryKey;
	/**
	 * The file on screen, for analytics. Without it, the viewer reads it from the signed URL
	 * once that loads.
	 */
	fileId?: FileId;
};

/**
 * The signed URL for viewing a file, fetched once per control and refreshed on demand. A cached
 * URL never goes stale on its own: swapping it would reload the document on screen, and a load
 * that fails on an expired URL refreshes it.
 */
export const useDocumentUrl = (control: Option<PDFViewerControl>) => {
	const { get } = useRequest();
	const queryClient = useQueryClient();
	const queryKey = useMemo<QueryKey>(
		() =>
			Option.isSome(control)
				? documentUrlQueryKey(control.queryKey, "view")
				: noDocumentUrlQueryKey(),
		[control],
	);
	const fetchUrl = control?.fetchUrl;
	const query = useQuery({
		queryKey,
		queryFn: async (): Promise<string> =>
			await get(`${fetchUrl}?download=false`, undefined, "text"),
		enabled: Option.isSome(fetchUrl),
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: 1000 * 60 * 3,
	});
	/** Refetches the signed URL, resolving it, or none when the fetch fails. */
	const refresh = useCallback(async (): Promise<Option<string>> => {
		await queryClient.invalidateQueries({ queryKey });
		const state = queryClient.getQueryState<string>(queryKey);
		return state?.status === "success" ? state.data : undefined;
	}, [queryClient, queryKey]);
	return {
		url: query.data,
		error: query.error,
		isLoading: query.isLoading,
		isRefreshing: query.isFetching && !query.isLoading,
		refresh,
	};
};
